import type { ConsentChoice, SiteConfig } from '@cmp/shared';

/**
 * Google Consent Mode v2 and Microsoft UET consent signals.
 * The default call must run before gtag.js / GTM loads, so `installDefaults` is invoked
 * synchronously at script start, before any config fetch.
 */

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
    uetq?: unknown[];
  }
}

function gtag(...args: unknown[]): void {
  window.dataLayer = window.dataLayer || [];
  // gtag() must push the `arguments` object, not an array, or GTM ignores the command.
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

const GRANTED = 'granted';
const DENIED = 'denied';

export function installDefaults(waitForUpdate: number, adsDataRedaction: boolean, urlPassthrough: boolean): void {
  if (!window.gtag) window.gtag = gtag;
  gtag('consent', 'default', {
    ad_storage: DENIED,
    ad_user_data: DENIED,
    ad_personalization: DENIED,
    analytics_storage: DENIED,
    functionality_storage: DENIED,
    personalization_storage: DENIED,
    security_storage: GRANTED,
    wait_for_update: waitForUpdate,
  });
  if (adsDataRedaction) gtag('set', 'ads_data_redaction', true);
  if (urlPassthrough) gtag('set', 'url_passthrough', true);
}

export function toGcm(c: ConsentChoice): Record<string, string> {
  return {
    ad_storage: c.marketing ? GRANTED : DENIED,
    ad_user_data: c.marketing ? GRANTED : DENIED,
    ad_personalization: c.marketing ? GRANTED : DENIED,
    analytics_storage: c.analytics ? GRANTED : DENIED,
    functionality_storage: c.functionality ? GRANTED : DENIED,
    personalization_storage: c.functionality ? GRANTED : DENIED,
    security_storage: GRANTED,
  };
}

export function update(c: ConsentChoice, config: Pick<SiteConfig, 'consentMode' | 'uet'>): void {
  if (config.consentMode.enabled) gtag('consent', 'update', toGcm(c));
  if (config.uet) {
    window.uetq = window.uetq || [];
    window.uetq.push('consent', 'update', { ad_storage: c.marketing ? GRANTED : DENIED });
  }
}

export function installUetDefault(): void {
  window.uetq = window.uetq || [];
  window.uetq.push('consent', 'default', { ad_storage: DENIED });
}
